import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parcelsApi } from '@/rest/modules/parcels-api';
import { Parcel, ParcelFilters } from '@/domain/parcels/parcel';
import { ApiResponse } from '@/domain';
import { useToast } from '../use-toast';
import { onErrorGenericResponse } from '@/utils/hookUtils';

export const useParcels = (filters?: ParcelFilters) => {
  return useQuery({
    queryKey: ['parcels', filters],
    queryFn: () => parcelsApi.getParcels(filters),
  });
};

export const useParcel = (id: string) => {
  return useQuery({
    queryKey: ['parcel', id],
    queryFn: () => parcelsApi.getParcel(id),
    enabled: !!id,
  });
};

export const useUpdateParcel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Parcel> }) =>
      parcelsApi.updateParcel(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parcels'] });
    },
  });
};

export const useUpdateParcelStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, state }: { id: string; state: string }) =>
      parcelsApi.updateParcelStatus(id, state),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parcels'] });
    },
    onError: onErrorGenericResponse
  });
};
export const useParcelByContract = ({ contractId, enabled = true }: { contractId: string, enabled?: boolean }) => {
  return useQuery({
    queryKey: ['parcels', 'contract', contractId],
    queryFn: () => parcelsApi.getParcelByContract(contractId),
    enabled: !!contractId && enabled,
  });
};

export const useParcelContractDetail = ({ contractId, parcelId, enabled = true }: { contractId: string, parcelId: string, enabled?: boolean }) => {
  return useQuery({
    queryKey: ['parcels', 'detail', contractId, parcelId],
    queryFn: () => parcelsApi.getParcelContractDetail(contractId, parcelId),
    enabled: !!contractId && !!parcelId && enabled,
    staleTime: 100
  });
};

export const useCreateNewEvent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ contractId, parcelId, data }: { contractId: string, parcelId: string, data: any }) =>
      parcelsApi.createNewEvent(contractId, parcelId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['parcels', 'detail', variables.contractId, variables.parcelId] });
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al agregar evento de modificación',
        variant: 'destructive',
      })
    },
  });
};

export const useDeleteParcelContractEvent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ contractId, parcelId, eventId }: { contractId: string, parcelId: string, eventId: string }) =>
      parcelsApi.deleteParcelContractEvent(contractId, parcelId, eventId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['parcels', 'detail', variables.contractId, variables.parcelId] });
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al eliminar evento de modificación',
        variant: 'destructive',
      })
    },
  });
};
